import { notify } from '@bynder/design-system';
import { catchResponseError } from 'packages/helpers/helpers';
import store from './store';
import ProxyServerConnector from 'packages/services/connectors/ProxyServerConnector';
import authService from 'packages/services/authService';
import { getUser, logout } from 'packages/store/user/user.actions';
import { refreshAccessToken } from 'packages/store/platform/platform.actions';
import { appPath } from 'packages/helpers/navigation';
import AuthenticationHelper from 'packages/helpers/AuthenticationHelper';
import type { ResponseModel } from 'packages/services/connectors/ResponseModel';

const initServerConnector = (): void => {
    // ProxyServerConnector.setCsrfToken();

    ProxyServerConnector.getToken = (): string => AuthenticationHelper.getToken();

    ProxyServerConnector.tokenRefresher = async (requestObject: Request): Promise<any> => {
        const sendHangedRequest = (): Promise<ResponseModel> => {
            requestObject.headers.set('Authorization', `Bearer ${ProxyServerConnector.getToken()}`);

            return ProxyServerConnector.sendRequestObject(requestObject);
        };

        if (ProxyServerConnector.isBlocked) {
            await ProxyServerConnector.waitUntilUnblock();

            return sendHangedRequest();
        }

        try {
            const refreshTokenResponsePromise = authService.refreshToken();
            ProxyServerConnector.isBlocked = true;
            const refreshTokenResponse = await refreshTokenResponsePromise;

            if (refreshTokenResponse.status === 200) {
                AuthenticationHelper.setToken(refreshTokenResponse.json.identityAccessToken);
                ProxyServerConnector.isBlocked = false;

                await store.dispatch(getUser());

                return sendHangedRequest();
            }

            if ([400, 404, 406].includes(refreshTokenResponse.status)) {
                store.dispatch(refreshAccessToken());

                return new Promise((resolve) => {
                    ProxyServerConnector.requestsWaitList.push(async () => {
                        resolve(await sendHangedRequest());
                    });
                });
            }

            return ProxyServerConnector.handleResponseStatus(
                new Request('/auth/refresh'),
                new Response('', { status: 401 }),
            );
        } catch (err) {
            catchResponseError(err);
        } finally {
            ProxyServerConnector.isBlocked = false;
        }
    };

    ProxyServerConnector.refreshToken = async (): Promise<boolean> => {
        // A refresh (usually from an HTTP 498) is already in flight; ride along with it. True here
        // only means that refresh finished, not that the token is valid: the flag is cleared in a
        // finally on failure too. The caller retries once and gives up on a second 498/401.
        if (ProxyServerConnector.isBlocked) {
            await ProxyServerConnector.waitUntilUnblock();

            return true;
        }

        try {
            // Start the request BEFORE raising the flag: _send awaits waitUntilUnblock(), so
            // blocking first would deadlock the refresh against itself.
            const refreshTokenResponsePromise = authService.refreshToken();
            ProxyServerConnector.isBlocked = true;
            const refreshTokenResponse = await refreshTokenResponsePromise;

            if (refreshTokenResponse.status !== 200) {
                return false;
            }

            AuthenticationHelper.setToken(refreshTokenResponse.json.identityAccessToken);

            return true;
        } catch (err) {
            catchResponseError(err);

            return false;
        } finally {
            ProxyServerConnector.isBlocked = false;
        }
    };

    window.addEventListener('onLogout', () => {
        store.dispatch(logout());
    });

    // TODO: these are full page reloads, which throw away app state and, when
    // Studio is embedded, reload the host's page too. They should go through
    // react-router's navigate() once it is reachable from outside React.
    window.addEventListener('onForbidden', () => {
        window.location.href = appPath('/no-access');
    });

    window.addEventListener('onNotFound', () => {
        window.location.href = appPath('/not-found');
    });

    window.addEventListener('onInternalError', () => {
        notify({
            title: 'Something went wrong',
            variant: 'error',
        });
    });
    window.addEventListener('onFailedToFetch', () => {
        notify({
            title: 'Something went wrong',
            variant: 'error',
        });
    });
    window.addEventListener('onFailedToParseJson', () => {
        notify({
            title: 'Something went wrong',
            variant: 'error',
        });
    });
};

export default initServerConnector;
